/**
 * 欢迎语管理 Composable
 * 管理欢迎语的获取、显示、动画等功能
 */

import axios from 'axios';
import config from '@/config';
import logger from '@/utils/logger';
import { getWelcomeConfigByStoreId, getUserStoreIds, removeWelcomeConfigByStoreId } from '@/utils/auth';
import { parseApiResponse, handleApiError, snakeToCamel } from '@/utils/apiResponse';

export default function useWelcomeMessage() {
  return {
    data() {
      return {
        welcomeMessage: '', // 存储启用的欢迎语
        welcomeConfig: null, // 存储欢迎语配置信息（包含样式）
        welcomeMessageTimer: null, // 欢迎语轮询定时器
        lastWelcomeMessage: '', // 存储上一次的欢迎语，用于检测变化
        lastWelcomeConfig: null, // 存储上一次的欢迎语配置，用于检测 isFullscreen 变化
        welcomeAnimationState: '', // 欢迎语动画状态: 'entering', 'leaving', ''
        welcomeAnimationTimer: null // 欢迎语动画定时器
      };
    },

    computed: {
      // 欢迎语样式计算属性
      welcomeMessageStyle() {
        if (!this.welcomeConfig) {
          return {
            fontSize: '2.5rem',
            color: 'white',
            fontWeight: 'bold'
          };
        }
        
        // 处理fontSize：如果是数字则添加px单位，否则直接使用
        const fontSize = this.welcomeConfig.fontSize 
          ? (typeof this.welcomeConfig.fontSize === 'number' 
             ? `${this.welcomeConfig.fontSize}px` 
             : this.welcomeConfig.fontSize)
          : '2.5rem';
        
        return {
          fontSize: fontSize,
          color: this.welcomeConfig.fontColor || 'white',
          fontWeight: 'bold'
        };
      }
    },

    methods: {
      /**
       * 播放欢迎语进入动画
       */
      playWelcomeEnterAnimation() {
        this.welcomeAnimationState = 'entering';
        if (this.welcomeAnimationTimer) {
          clearTimeout(this.welcomeAnimationTimer);
        }
        this.welcomeAnimationTimer = setTimeout(() => {
          this.welcomeAnimationState = '';
          this.welcomeAnimationTimer = null;
        }, 500); // 动画持续500ms
      },

      /**
       * 播放欢迎语退出动画
       */
      playWelcomeLeaveAnimation() {
        this.welcomeAnimationState = 'leaving';
        if (this.welcomeAnimationTimer) {
          clearTimeout(this.welcomeAnimationTimer);
        }
        this.welcomeAnimationTimer = setTimeout(() => {
          this.welcomeMessage = '';
          this.welcomeAnimationState = '';
          this.welcomeAnimationTimer = null;
        }, 500); // 动画持续500ms
      },

      /**
       * 获取欢迎语
       */
      async fetchWelcomeMessage() {
        if (this.isSliding) {
          return; // 滑动期间暂停欢迎语更新，避免触发不必要渲染
        }
        
        const storeId = this.effectiveStoreId;
        if (!storeId) {
          // 如果没有门店ID，尝试从登录信息中获取
          const storeIds = getUserStoreIds();
          if (storeIds.length === 0) {
            logger.warn('未找到门店ID，无法获取欢迎配置');
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            this.welcomeConfig = null;
            this.handleWelcomeMessageChange('');
            return;
          }
        }
        
        // 优先调用 API 获取最新数据，确保与数据库同步
        try {
          const apiUrl = config.getApiUrl(
            config.apiEndpoints.welcomeConfig.enabled,
            storeId
          );
          
          const response = await axios.get(apiUrl);
          
          // 接口返回 ApiResponse 格式：{ code: 200, message: "...", data: {...} }
          const result = parseApiResponse(response);
          
          if (!result.success || !result.data) {
            logger.warn('欢迎语接口返回数据为空');
            // 清除缓存中的欢迎配置
            removeWelcomeConfigByStoreId(storeId);
            // 更新 lastWelcomeConfig
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            // 清空 welcomeConfig
            this.welcomeConfig = null;
            this.handleWelcomeMessageChange('');
            return;
          }
          
          // 将下划线字段名转换为驼峰命名
          const welcomeConfig = snakeToCamel(result.data);
          
          // 关键修复：检查 is_enabled 字段，只有当启用时才显示欢迎语
          const isEnabled = welcomeConfig.isEnabled !== undefined 
            ? (welcomeConfig.isEnabled === true || welcomeConfig.isEnabled === 1 || welcomeConfig.isEnabled === '1')
            : true; // 如果没有 is_enabled 字段，默认启用（向后兼容）
          
          if (!isEnabled) {
            logger.debug('欢迎语未启用，不显示欢迎语', {
              storeId,
              isEnabled: welcomeConfig.isEnabled
            });
            // 如果未启用，清空欢迎语
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            this.welcomeConfig = null;
            this.handleWelcomeMessageChange('');
            return;
          }
          
          // 存储上一次的配置，用于检测变化
          this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
          
          // 存储完整的配置信息（包含 isFullscreen 字段），确保 isFullscreen 为数字类型
          this.welcomeConfig = {
            ...welcomeConfig,
            isFullscreen: welcomeConfig.isFullscreen !== undefined 
              ? (typeof welcomeConfig.isFullscreen === 'number' 
                  ? welcomeConfig.isFullscreen 
                  : (welcomeConfig.isFullscreen === true || welcomeConfig.isFullscreen === 'true' ? 1 : parseInt(welcomeConfig.isFullscreen) || 0))
              : 0
          };
          
          // 根据接口返回的数据结构处理欢迎语
          let newWelcomeMessage = '';
          
          // 处理API返回的welcomeText字段（已转换为驼峰命名）
          if (welcomeConfig.welcomeText) {
            newWelcomeMessage = welcomeConfig.welcomeText;
          }
          
          // 处理欢迎语状态变化
          this.handleWelcomeMessageChange(newWelcomeMessage);
          
        } catch (error) {
          // 接口调用失败时的处理
          const errorResult = handleApiError(error);
          
          if (error.response && error.response.status === 404) {
            // 404表示该门店没有配置欢迎语，清除缓存
            removeWelcomeConfigByStoreId(storeId);
            // 更新 lastWelcomeConfig
            this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
            // 清空 welcomeConfig
            this.welcomeConfig = null;
            this.handleWelcomeMessageChange('');
          } else {
            // 其他错误（网络错误等），fallback 到缓存配置作为降级方案
            const loginWelcomeConfig = getWelcomeConfigByStoreId(storeId);
            if (loginWelcomeConfig) {
              // 使用登录时返回的欢迎配置作为降级方案
              this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
              
              // 存储完整的配置信息（包含 isFullscreen 字段），确保 isFullscreen 为数字类型
              this.welcomeConfig = {
                ...loginWelcomeConfig,
                isFullscreen: loginWelcomeConfig.isFullscreen !== undefined 
                  ? (typeof loginWelcomeConfig.isFullscreen === 'number' 
                      ? loginWelcomeConfig.isFullscreen 
                      : parseInt(loginWelcomeConfig.isFullscreen) || 0)
                  : 0
              };
              
              // 处理欢迎语内容（兼容下划线和驼峰命名）
              const newWelcomeMessage = loginWelcomeConfig.welcomeText || loginWelcomeConfig.welcome_text || '';
              this.handleWelcomeMessageChange(newWelcomeMessage);
            } else {
              // 既没有API数据，也没有缓存，清空欢迎语
              this.lastWelcomeConfig = this.welcomeConfig ? { ...this.welcomeConfig } : null;
              this.welcomeConfig = null;
              this.handleWelcomeMessageChange('');
            }
          }
        }
      },

      /**
       * 处理欢迎语状态变化的统一方法
       */
      handleWelcomeMessageChange(newWelcomeMessage) {
        // 检测 isFullscreen 是否发生变化
        const oldIsFullscreen = this.lastWelcomeConfig && 
          (this.lastWelcomeConfig.isFullscreen === 1 || this.lastWelcomeConfig.isFullscreen === '1');
        const newIsFullscreen = this.welcomeConfig && 
          (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
        const isFullscreenChanged = oldIsFullscreen !== newIsFullscreen;
        
        // 检测欢迎语内容是否发生变化
        const welcomeMessageChanged = this.lastWelcomeMessage !== newWelcomeMessage;
        
        // 如果欢迎语内容或 isFullscreen 发生变化，都需要处理
        if (welcomeMessageChanged || isFullscreenChanged) {
          // 更新状态
          this.lastWelcomeMessage = newWelcomeMessage;
          
          // 获取 isFullscreen 配置（0=半屏，1=全屏），支持数字和字符串类型
          const isFullscreen = this.welcomeConfig && 
            (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
          
          // 如果是全屏模式，不显示半屏欢迎语
          if (isFullscreen) {
            this.welcomeMessage = ''; // 全屏模式下不显示半屏欢迎语
            if (this.maxDisplayCount !== undefined) {
              this.maxDisplayCount = 4; // 全屏模式下每页显示4个患者模块
            }
            
            // 如果从半屏切换到全屏，且当前在患者模块页面，需要检查是否需要切换到全屏欢迎语页
            // 注意：初始化时（lastWelcomeConfig === null）不执行页面切换，确保从患者页开始
            if (isFullscreenChanged && !oldIsFullscreen && this.lastWelcomeConfig !== null) {
              // 计算治疗中患者数量
              const ongoingPatients = this.activeAppointments.filter(item => 
                this.getTreatmentStatus(item) === '治疗中'
              );
              // 全屏模式下每页4个
              const patientPages = Math.ceil(ongoingPatients.length / 4);
              // 如果当前在最后一页患者模块，立即切换到全屏欢迎语页
              if (this.currentPage === patientPages - 1) {
                this.currentPage = patientPages;
                if (this.addSystemLog) {
                  this.addSystemLog('从半屏模式切换到全屏模式，切换到全屏欢迎语页');
                }
              }
            }
          } else {
            // 半屏模式，使用原有逻辑
            if (newWelcomeMessage && !this.welcomeMessage) {
              // 从无到有：播放进入动画
              this.welcomeMessage = newWelcomeMessage;
              this.playWelcomeEnterAnimation();
              if (this.addSystemLog) {
                this.addSystemLog(`门店${this.effectiveStoreId}欢迎语已启用并显示（半屏模式）`);
              }
            } else if (!newWelcomeMessage && this.welcomeMessage) {
              // 从有到无：播放退出动画
              this.playWelcomeLeaveAnimation();
              if (this.addSystemLog) {
                this.addSystemLog(`门店${this.effectiveStoreId}欢迎语已禁用并隐藏`);
              }
            } else if (newWelcomeMessage && this.welcomeMessage && newWelcomeMessage !== this.welcomeMessage) {
              // 内容变化：直接更新
              this.welcomeMessage = newWelcomeMessage;
              if (this.addSystemLog) {
                this.addSystemLog(`门店${this.effectiveStoreId}欢迎语内容已更新`);
              }
            }
            if (this.maxDisplayCount !== undefined) {
              this.maxDisplayCount = newWelcomeMessage ? 2 : 4;
            }
            
            // 如果从全屏切换到半屏，且当前在全屏欢迎语页，需要切换回患者模块页
            // 注意：初始化时（lastWelcomeConfig === null）不执行页面切换，确保从患者页开始
            if (isFullscreenChanged && oldIsFullscreen && this.lastWelcomeConfig !== null) {
              // 计算治疗中患者数量
              const ongoingPatients = this.activeAppointments.filter(item => 
                this.getTreatmentStatus(item) === '治疗中'
              );
              // 半屏模式下每页2个
              const patientPages = Math.ceil(ongoingPatients.length / 2);
              // 如果当前在全屏欢迎语页，切换回最后一页患者模块
              if (this.currentPage === patientPages) {
                this.currentPage = Math.max(0, patientPages - 1);
                if (this.addSystemLog) {
                  this.addSystemLog('从全屏模式切换到半屏模式，切换回患者模块页');
                }
                // 重新初始化图表
                this.$nextTick(() => {
                  if (this.initCharts) {
                    this.initCharts();
                  }
                });
              }
            }
          }
        } else {
          // 即使欢迎语内容没变，也要检查 isFullscreen 是否变化
          const isFullscreen = this.welcomeConfig && 
            (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1');
          if (isFullscreen) {
            this.welcomeMessage = '';
            if (this.maxDisplayCount !== undefined) {
              this.maxDisplayCount = 4;
            }
          } else {
            if (this.maxDisplayCount !== undefined) {
              this.maxDisplayCount = newWelcomeMessage ? 2 : 4;
            }
          }
        }
      }
    }
  };
}

